import type { LogsEvent } from '@datadog/browser-logs';
import type { RumEvent } from '@datadog/browser-rum';

/**
 * Browser-side counterpart of the server's LogRedactor. Browser logs, RUM events and session replay
 * all leave the user's machine for a third-party store, so before anything is sent URLs lose their
 * query strings (SSO tickets, redirect targets, tokens), free text loses credential- and
 * identifier-shaped fragments, and objects logged with console.error are reduced to a one-line summary.
 * The rules mirror docs/security/logging-data-classification-standard.md.
 */

export const REDACTED = '[REDACTED]';

const URL_QUERY = /(https?:\/\/[^\s"'<>?#]+)\?[^\s"'<>#]*/gi;
const AUTH_SCHEME = /\b(Bearer|Basic)\s+[A-Za-z0-9\-._~+/=]{16,}/gi;
const JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*/g;
const KEY_VALUE = /\b(access_token|token|ssoToken|jwt|password|secret|api[_-]?key|mfaCode|SAMLResponse|redirectUrl|Authorization|Cookie)("?\s*[=:]\s*"?)[^&\s"';,}]+/gi;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const SSN = /\b\d{3}-\d{2}-\d{4}\b/g;

/** Origin and path only. The query string and fragment are dropped entirely. */
export function redactUrl(url: string | null | undefined): string {
    if (!url) { return ''; }
    try {
        const parsed = new URL(url, globalThis.location?.origin ?? 'https://localhost');
        return parsed.origin + parsed.pathname;
    } catch {
        return url.split(/[?#]/)[0];
    }
}

/** Removes credential- and identifier-shaped fragments from free text. */
export function redactText(text: string | null | undefined): string {
    if (!text) { return ''; }
    return text
        .replace(URL_QUERY, '$1?[REDACTED-QUERY]')
        .replace(AUTH_SCHEME, '$1 ' + REDACTED)
        .replace(JWT, '[REDACTED-JWT]')
        .replace(KEY_VALUE, '$1$2' + REDACTED)
        .replace(EMAIL, '[REDACTED-EMAIL]')
        .replace(SSN, '[REDACTED-SSN]');
}

interface UserLike {
    id?: number | string | null;
    firstName?: string | null;
    lastName?: string | null;
}

/**
 * The user context sent to Datadog RUM and browser logs: the staff member's id and display name, so
 * sessions can be searched by who was using them. Contact details (e-mail, phone) stay out.
 */
export function toDatadogUser(user: UserLike | null | undefined): { id: string; name?: string } | undefined {
    if (user?.id == null) { return undefined; }
    const name = [user.firstName, user.lastName].map(part => part?.trim()).filter(Boolean).join(' ');
    return name ? { id: String(user.id), name } : { id: String(user.id) };
}

interface ErrorLike {
    name?: unknown;
    message?: unknown;
    status?: unknown;
    url?: unknown;
    stack?: unknown;
    headers?: { get?: (name: string) => string | null };
    originalError?: unknown;
}

/**
 * The context sent with an error log. An Angular HttpErrorResponse carries the full request URL, the
 * response headers and the response body; only the status, the path, the error kind, a redacted
 * message and the server's correlation id are kept.
 */
export function sanitizeErrorContext(err: unknown): Record<string, unknown> {
    const error = (err ?? {}) as ErrorLike;
    const original = (error.originalError ?? {}) as ErrorLike;
    const message = typeof error.message === 'string'
        ? error.message
        : typeof original.message === 'string' ? original.message : 'Unhandled error';
    const stack = typeof error.stack === 'string'
        ? error.stack
        : typeof original.stack === 'string' ? original.stack : undefined;
    const kind = typeof error.name === 'string'
        ? error.name
        : typeof original.name === 'string' ? original.name : 'Error';

    let correlationId: string | undefined;
    try { correlationId = error.headers?.get?.('X-Correlation-Id') ?? undefined; } catch { correlationId = undefined; }

    return {
        message: redactText(message),
        error: { kind, stack: stack ? redactText(stack) : undefined },
        http: {
            status_code: typeof error.status === 'number' ? error.status : undefined,
            url: typeof error.url === 'string' ? redactUrl(error.url) : undefined,
        },
        correlationId,
    };
}

let consoleErrorScrubbing = true;

/**
 * PCG Admin Settings "Scrub Console Errors Sent to Datadog". main.ts sets it from the user info before
 * RUM starts; until then, and whenever that call fails, it stays on.
 */
export function setConsoleErrorScrubbing(on: boolean): void { consoleErrorScrubbing = on; }

/**
 * A console.error argument that is an object reaches Datadog as pretty-printed JSON inside the message.
 * An Angular HttpErrorResponse brings its response body, headers and full URL with it, and any other
 * object can hold record data, so each one is replaced by a one-line summary. The text around it stays.
 */
export function scrubConsoleMessage(message: string | null | undefined): string {
    if (!message) { return ''; }
    let scrubbed = '';
    let index = 0;
    while (index < message.length) {
        const start = findJsonStart(message, index);
        if (start < 0) { return scrubbed + message.slice(index); }
        const end = findJsonEnd(message, start);
        // An unclosed object was truncated, so nothing from it on is kept.
        if (end < 0) { return scrubbed + message.slice(index, start) + '[object]'; }
        const candidate = message.slice(start, end + 1);
        scrubbed += message.slice(index, start) + (summariseJson(candidate) ?? candidate);
        index = end + 1;
    }
    return scrubbed;
}

/** An object or array argument starts the message or follows the space the SDK joins arguments with. */
function findJsonStart(text: string, from: number): number {
    for (let i = from; i < text.length; i++) {
        if ((text[i] === '{' || text[i] === '[') && (i === 0 || /\s/.test(text[i - 1]))) { return i; }
    }
    return -1;
}

/** Index of the bracket that closes the one at `start`, ignoring brackets inside strings; -1 if none. */
function findJsonEnd(text: string, start: number): number {
    let depth = 0;
    let inString = false;
    for (let i = start; i < text.length; i++) {
        const char = text[i];
        if (inString) {
            if (char === '\\') { i++; }
            else if (char === '"') { inString = false; }
        }
        else if (char === '"') { inString = true; }
        else if (char === '{' || char === '[') { depth++; }
        else if ((char === '}' || char === ']') && --depth === 0) { return i; }
    }
    return -1;
}

/** One line in place of a logged object, or undefined when the text was not JSON after all. */
function summariseJson(json: string): string | undefined {
    let value: unknown;
    try { value = JSON.parse(json); } catch { return undefined; }
    if (Array.isArray(value)) { return '[array]'; }
    if (!value || typeof value !== 'object') { return undefined; }
    const object = value as Record<string, unknown>;
    if (object['name'] === 'HttpErrorResponse' || (typeof object['status'] === 'number' && typeof object['url'] === 'string')) {
        const url = typeof object['url'] === 'string' ? redactUrl(object['url']) : undefined;
        return `[${['HttpErrorResponse', object['status'], object['statusText'], url].filter((part) => part !== undefined && part !== '').join(' ')}]`;
    }
    if (typeof object['name'] === 'string' && typeof object['message'] === 'string') { return `[${object['name']}: ${redactText(object['message'])}]`; }
    return '[object]';
}

interface MutableEvent {
    type?: string;
    origin?: string;
    message?: string;
    view?: { url?: string; referrer?: string };
    http?: { url?: string };
    resource?: { url?: string };
    error?: { message?: string; stack?: string; source?: string; resource?: { url?: string } };
    action?: { target?: { name?: string } };
}

function sanitizeCommon(event: MutableEvent): void {
    if (event.view?.url) { event.view.url = redactUrl(event.view.url); }
    if (event.view?.referrer) { event.view.referrer = redactUrl(event.view.referrer); }
    if (event.error?.message) { event.error.message = redactText(event.error.message); }
    if (event.error?.stack) { event.error.stack = redactText(event.error.stack); }
    if (event.error?.resource?.url) { event.error.resource.url = redactUrl(event.error.resource.url); }
}

/** `beforeSend` for @datadog/browser-logs. */
export function sanitizeLogEvent(event: LogsEvent): boolean {
    const mutable = event as unknown as MutableEvent;
    if (typeof mutable.message === 'string') {
        // A forwarded console.error carries its arguments, logged objects included, in the message.
        mutable.message = redactText(mutable.origin === 'console' && consoleErrorScrubbing ? scrubConsoleMessage(mutable.message) : mutable.message);
    }
    if (mutable.http?.url) { mutable.http.url = redactUrl(mutable.http.url); }
    sanitizeCommon(mutable);
    return true;
}

/** `beforeSend` for @datadog/browser-rum. */
export function sanitizeRumEvent(event: RumEvent): boolean {
    const mutable = event as unknown as MutableEvent;
    if (mutable.resource?.url) { mutable.resource.url = redactUrl(mutable.resource.url); }
    if (mutable.action?.target?.name) { mutable.action.target.name = redactText(mutable.action.target.name); }
    // A console.error recorded as a RUM error carries its arguments, logged objects included, in the message.
    if (consoleErrorScrubbing && mutable.error?.source === 'console' && mutable.error.message) { mutable.error.message = scrubConsoleMessage(mutable.error.message); }
    sanitizeCommon(mutable);
    return true;
}
